import { backofficeApi } from "@/services/backofficeApi";
import { getSearchParamForRequest } from "@/utils/filters";

export interface OrganizationItem {
	id: string;
	name: string;
	description: string;
	userCount: number;
	createdAt: string;
	updatedAt: string;
}

export type OrganizationsResponse = Dryp.ApiList<OrganizationItem>;

export interface CreateOrganizationRequest {
	name: string;
	description?: string | null;
}

export interface BackofficeUser {
	id: string;
	userName: string;
	email: string;
	roleName: string;
	organizationCount: number;
}

export type BackofficeUsersResponse = Dryp.ApiList<BackofficeUser>;

export interface UserOrganizationItem {
	organizationId: string;
	organizationName: string;
	roleName: string;
	primaryOrganization: boolean;
}

export const backoffice = {
	getOrganizations: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		search?: string,
	): Promise<OrganizationsResponse> => {
		return backofficeApi.get<OrganizationsResponse>(`/organizations`, {
			params: { ...pagination, ...sort, ...getSearchParamForRequest(search) },
		});
	},
	createOrganization: async (data: CreateOrganizationRequest): Promise<void> => {
		return backofficeApi.post<void>(`/organizations`, data);
	},
	updateOrganization: async (id: string, data: CreateOrganizationRequest): Promise<void> => {
		return backofficeApi.put<void>(`/organizations/${id}`, data);
	},
	getUsers: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		search?: string,
		organizationIds?: string[],
		roleNames?: string[],
	): Promise<BackofficeUsersResponse> => {
		return backofficeApi.get<BackofficeUsersResponse>(`/users`, {
			params: {
				...pagination,
				...sort,
				...getSearchParamForRequest(search),
				...(organizationIds && organizationIds.length > 0
					? { "filter[organizationIds]": organizationIds }
					: {}),
				...(roleNames && roleNames.length > 0 ? { "filter[roleNames]": roleNames } : {}),
			},
		});
	},
	getUserOrganizations: async (userId: string): Promise<UserOrganizationItem[]> => {
		return backofficeApi.get<UserOrganizationItem[]>(`/user-organizations`, {
			params: { userId },
		});
	},
	addUserOrganizations: async (userId: string, organizations: Record<string, string>): Promise<void> => {
		return backofficeApi.post<void>(`/user-organizations`, { userId, organizations });
	},
	updateUserOrganizations: async (userId: string, organizations: Record<string, string>): Promise<void> => {
		return backofficeApi.put<void>(`/user-organizations`, { userId, organizations });
	},
	removeUserOrganizations: async (userId: string, organizations: string[]): Promise<void> => {
		return backofficeApi.delete<void>(`/user-organizations`, {
			data: { userId, organizations },
		});
	},
	setPrimaryUserOrganization: async (userId: string, organizationId: string): Promise<void> => {
		return backofficeApi.put<void>(`/user-organizations/primary`, { userId, organizationId });
	},
	updateUserPassword: async (userId: string, password: string): Promise<void> => {
		return backofficeApi.put<void>(`/users/${userId}/password`, { password });
	},
};
